import {
  PATTERN_SLOTS,
  type Pattern,
  type PatternId,
  type TrackNumber,
  formatPatternId,
} from "@midi-fantom-utils/midi-util";
import type { SoundfontSource } from "@midi-fantom-utils/player-core";
import type { MidiPortSelection, SoundOutput } from "../config.js";
import { PatternMiniPlayer } from "./PatternMiniPlayer.js";

export type PatternScreenProps = {
  patterns: ReadonlyMap<PatternId, Pattern>;
  source: SoundfontSource;
  output: SoundOutput;
  midiPort: MidiPortSelection | null;
  onEdit: (patternId: PatternId) => void;
  onDelete: (patternId: PatternId) => void;
};

const TRACK_NUMBERS: TrackNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

/**
 * Грид слотов паттернов в ориентации оригинального интерфейса Fantom:
 * ось X (горизонталь) — треки 1..16 (все, без переключателя), ось Y
 * (вертикаль) — вариации A..H. Ячейка = `T{track}-{slot}`.
 */
export const PatternScreen = ({
  patterns,
  source,
  output,
  midiPort,
  onEdit,
  onDelete,
}: PatternScreenProps) => {
  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <h2 className="card__title" style={{ margin: 0 }}>Слоты паттернов (Fantom)</h2>
        <span className="muted" style={{ fontSize: 12 }}>
          16 треков × 8 вариаций = 128 слотов
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12,
          }}
        >
          <thead>
            <tr>
              <th style={cellStyle("head")}>Вариация \ Трек</th>
              {TRACK_NUMBERS.map((t) => (
                <th key={t} style={cellStyle("rowHead")}>{t}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PATTERN_SLOTS.map((slot) => (
              <tr key={slot}>
                <td style={cellStyle("slotHead")}>{slot}</td>
                {TRACK_NUMBERS.map((t) => {
                  const id = formatPatternId(t, slot);
                  const pat = patterns.get(id);
                  return (
                    <td key={t} style={cellStyle(pat ? "filled" : "empty")}>
                      {pat ? (
                        <PatternCell
                          pat={pat}
                          source={source}
                          output={output}
                          midiPort={midiPort}
                          onEdit={onEdit}
                          onDelete={onDelete}
                        />
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>·</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const cellStyle = (kind: "head" | "rowHead" | "slotHead" | "empty" | "filled"): React.CSSProperties => {
  const base: React.CSSProperties = {
    border: "1px solid var(--border)",
    padding: 6,
    verticalAlign: "top",
  };
  if (kind === "head") return { ...base, color: "var(--text-muted)", fontWeight: 500 };
  if (kind === "rowHead")
    return { ...base, fontWeight: 600, color: "var(--text-muted)", width: 28, textAlign: "center" };
  if (kind === "slotHead")
    return { ...base, fontWeight: 600, color: "var(--text-muted)", width: 28, textAlign: "center" };
  if (kind === "empty")
    return {
      ...base,
      color: "var(--text-muted)",
      background: "var(--surface-2)",
      textAlign: "center",
    };
  return base;
};

const PatternCell = ({
  pat,
  source,
  output,
  midiPort,
  onEdit,
  onDelete,
}: {
  pat: Pattern;
  source: SoundfontSource;
  output: SoundOutput;
  midiPort: MidiPortSelection | null;
  onEdit: (id: PatternId) => void;
  onDelete: (id: PatternId) => void;
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <div style={{ fontWeight: 600 }}>{pat.name}</div>
    <div className="muted">
      {pat.lengthMeasures} тактов · {pat.noteCount} нот
    </div>
    <div style={{ display: "flex", gap: 4 }}>
      <PatternMiniPlayer
                        pattern={pat}
                        source={source}
                        output={output}
                        midiPort={midiPort}
                      />
      <button
        type="button"
        className="btn"
        style={{ padding: "2px 6px", fontSize: 11 }}
        onClick={() => onEdit(pat.id)}
        aria-label="Редактировать"
      >
        ✎
      </button>
      <button
        type="button"
        className="btn btn--ghost"
        style={{ padding: "2px 6px", fontSize: 11 }}
        onClick={() => onDelete(pat.id)}
        aria-label="Удалить"
      >
        🗑
      </button>
    </div>
  </div>
);
